import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ListItemCardCst from "@components/ListItemCardCst";
import { useCardActions } from "@hooks/useCardActions";
import { CatalogEntityType } from "../types";
import { mapEntityToCard } from "../mapEntityToCard";
import { openEnrollmentModal } from "../Enrollment/host";
import { openAssociateInitiativesModal } from "../AssociateInitiatives/host";
import { openUsersAndGroupsModal } from "../UsersAndGroups/host";
import { UsersAndGroupsTab } from "../UsersAndGroups/types";

interface CatalogItemProps {
  entity: any;
  entityType: CatalogEntityType;
  selected: boolean;
  onSelect: (next: boolean) => void;
  onDetails: (entity: any) => void;
  onPublish?: (entity: any) => void;

  onEdit?: (entity: any) => void;
  onEnrolled?: () => void;
}

const CatalogItem: React.FC<CatalogItemProps> = ({
  entity,
  entityType,
  selected,
  onSelect,
  onDetails,
  onPublish,
  onEdit,
  onEnrolled,
}) => {
  const { t } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const { id: corporateId } = useParams();
  const navigate = useNavigate();

  const overrides = useMemo(() => {
    const base: Record<string, () => void> = {
      enroll: () =>
        corporateId &&
        openEnrollmentModal({
          objectType: entityType,
          objectId: entity.id,
          objectTitle: entity.title || entity.name || undefined,
          corporateId,
          isEcm: !!(entity.ecm_specialization || entity.toj_specialization),
          onSuccess: onEnrolled,
        }),
      "associate-initiatives": () =>
        corporateId &&
        openAssociateInitiativesModal({
          objectType: entityType,
          objectId: entity.id,
          objectTitle: entity.title || entity.name || undefined,
          corporateId,
          initialAssociatedIds: Array.isArray(entity.initiative)
            ? entity.initiative
                .map((i: any) => (typeof i === "object" ? i?.id : i))
                .filter((id: any): id is number => typeof id === "number")
            : [],
          onSuccess: onEnrolled,
        }),
    };
    const openUsersGroups = (initialTab: UsersAndGroupsTab) =>
      corporateId &&
      openUsersAndGroupsModal({
        initialTab,
        objectType: entityType,
        objectId: entity.id,
        objectTitle: entity.title || entity.name || undefined,
        corporateId,
      });

    base["enrolled-users"] = () => openUsersGroups("enrolled-users");
    base["pre-enrolled-users"] = () => openUsersGroups("pre-enrolled-users");
    base["waiting-users"] = () => openUsersGroups("waiting-users");
    base["enrolled-groups"] = () => openUsersGroups("enrolled-groups");

    base["see-editions"] = () => {
      if (!corporateId) return;
      const kind =
        (entity.type || entity.learning_object_type) === "DA_VIRTUAL_CLASS"
          ? "virtual"
          : "physical";
      navigate(
        `/v2/corporate/${corporateId}/detail/catalog/activities/${entity.id}/editions?kind=${kind}`,
      );
    };

    base["duplicate"] = () => {
      if (!corporateId) return;
      const segment =
        entityType === "activity"
          ? "activities"
          : entityType === "course"
            ? "courses"
            : "paths";
      navigate(
        `/v2/corporate/${corporateId}/detail/catalog/${segment}/${entity.id}/duplicate`,
      );
    };

    if (onEdit) base.edit = () => onEdit(entity);
    return base;
  }, [onEdit, entity, entityType, corporateId, navigate, onEnrolled]);

  const moreActions = useCardActions(entityType, entity, {
    corporateId,
    overrides,
  });

  const cardProps = mapEntityToCard(entity, entityType, {
    t,
    tActivity,
    onDetails,
    onPublish: onPublish ? () => onPublish(entity) : undefined,
    onCreateEdition: corporateId
      ? (e) => {
          const kind =
            (e.type || e.learning_object_type) === "DA_VIRTUAL_CLASS"
              ? "virtual"
              : "physical";
          navigate(
            `/v2/corporate/${corporateId}/detail/catalog/activities/${e.id}/editions/add?kind=${kind}`,
          );
        }
      : undefined,
  });

  // TODO: pulsante "Dettagli" nascosto solo nel catalogo principale.
  // Per ripristinarlo rimuovere l'override `actions: []` qui sotto
  // (il bottone resta definito in mapEntityToCard).
  const cardPropsHiddenDetails = { ...cardProps, actions: [] };

  return (
    <ListItemCardCst
      {...cardPropsHiddenDetails}
      selectable
      selected={selected}
      onSelect={onSelect}
      moreActions={moreActions}
    />
  );
};

export default CatalogItem;
